import { useCallback, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { apiUrl } from '@/config/api.js';
import { useAuth } from '@/context/AuthContext.jsx';

const initialSettings = {
  highlightVisitedCells: true,
  onSixBehavior: 'moveImmediately',
};

const parseError = async (response, fallback = '') => {
  try {
    const data = await response.json();
    if (data?.error) {
      return data.error;
    }
  } catch (error) {
    // ignore
  }
  return fallback;
};

const GameSettingsPage = () => {
  const { t } = useTranslation('dashboard');
  const { token } = useAuth();
  const [settings, setSettings] = useState(initialSettings);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');
  const [formError, setFormError] = useState('');
  const [success, setSuccess] = useState('');
  const [pending, setPending] = useState(false);

  const loadSettings = useCallback(async () => {
    if (!token) {
      return;
    }
    setLoading(true);
    setLoadError('');
    try {
      const response = await fetch(apiUrl('/api/game-settings'), {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      if (!response.ok) {
        throw new Error(await parseError(response, t('gameplay.states.error')));
      }
      const data = await response.json();
      setSettings({
        highlightVisitedCells: Boolean(data.highlightVisitedCells),
        onSixBehavior: data.onSixBehavior,
      });
    } catch (error) {
      setLoadError(error.message);
    } finally {
      setLoading(false);
    }
  }, [t, token]);

  useEffect(() => {
    loadSettings();
  }, [loadSettings]);

  const handleCheckboxChange = (event) => {
    const { checked } = event.target;
    setSettings((prev) => ({ ...prev, highlightVisitedCells: checked }));
  };

  const handleBehaviorChange = (event) => {
    const { value } = event.target;
    setSettings((prev) => ({ ...prev, onSixBehavior: value }));
  };

  const handleSubmit = async (event) => {
    event.preventDefault();
    if (!token) {
      return;
    }
    setFormError('');
    setSuccess('');
    try {
      setPending(true);
      const response = await fetch(apiUrl('/api/game-settings'), {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify(settings),
      });
      if (!response.ok) {
        throw new Error(await parseError(response, t('gameplay.states.error')));
      }
      const data = await response.json();
      setSettings({
        highlightVisitedCells: Boolean(data.highlightVisitedCells),
        onSixBehavior: data.onSixBehavior,
      });
      setSuccess(t('gameplay.states.saved'));
    } catch (error) {
      setFormError(error.message);
    } finally {
      setPending(false);
    }
  };

  return (
    <div className="page-card">
      <div className="page-card-header">
        <div>
          <p className="page-caption">{t('gameplay.heading.caption')}</p>
          <h1>{t('gameplay.heading.title')}</h1>
          <p className="page-description">{t('gameplay.heading.description')}</p>
        </div>
      </div>
      {loading ? (
        <p className="page-description">{t('gameplay.states.loading')}</p>
      ) : loadError ? (
        <div className="game-form">
          <p className="form-error">{loadError}</p>
          <button type="button" className="secondary-button" onClick={loadSettings}>
            {t('gameplay.states.retry')}
          </button>
        </div>
      ) : (
        <form className="game-form" onSubmit={handleSubmit}>
          <label className="checkbox-field">
            <input
              type="checkbox"
              name="highlightVisitedCells"
              checked={settings.highlightVisitedCells}
              onChange={handleCheckboxChange}
            />
            <div>
              <p className="option-title">{t('gameplay.options.highlight.title')}</p>
              <p className="option-description">{t('gameplay.options.highlight.description')}</p>
            </div>
          </label>

          <div className="radio-group">
            <p className="option-title">{t('gameplay.options.onSix.title')}</p>
            <label className="radio-option">
              <input
                type="radio"
                name="onSixBehavior"
                value="moveImmediately"
                checked={settings.onSixBehavior === 'moveImmediately'}
                onChange={handleBehaviorChange}
              />
              <div>
                <p className="option-title">{t('gameplay.options.onSix.move.title')}</p>
                <p className="option-description">{t('gameplay.options.onSix.move.description')}</p>
              </div>
            </label>
            <label className="radio-option">
              <input
                type="radio"
                name="onSixBehavior"
                value="storeAndWait"
                checked={settings.onSixBehavior === 'storeAndWait'}
                onChange={handleBehaviorChange}
              />
              <div>
                <p className="option-title">{t('gameplay.options.onSix.store.title')}</p>
                <p className="option-description">{t('gameplay.options.onSix.store.description')}</p>
              </div>
            </label>
          </div>

          {formError && <p className="form-error">{formError}</p>}
          {success && <p className="form-success">{success}</p>}
          <button className="primary-button" type="submit" disabled={pending}>
            {pending ? t('gameplay.states.saving') : t('gameplay.states.save')}
          </button>
        </form>
      )}
    </div>
  );
};

export default GameSettingsPage;
